import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { adminApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ActionButtons, PaymentActionModal, can } from '../components/PaymentActions';

const usd = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const BUCKETS = {
  recoverable: { label: 'Recoverable', badge: 'bg-amber-100 text-amber-800', hint: 'Auth held — capture to collect' },
  expiringSoon: { label: 'Expiring <36h', badge: 'bg-red-100 text-red-800', hint: 'Capture now or it expires' },
  lost: { label: 'Lost / expired', badge: 'bg-gray-200 text-gray-700', hint: 'Auth expired — recharge a saved card' },
  drift: { label: 'Drift', badge: 'bg-purple-100 text-purple-800', hint: 'DB says paid, Stripe disagrees' },
  other: { label: 'Other', badge: 'bg-gray-100 text-gray-600', hint: '' },
  retrieve_error: { label: 'Stripe error', badge: 'bg-red-100 text-red-800', hint: 'Could not read PaymentIntent' },
};
const bucketOf = (it) => (it.stripeStatus === 'retrieve_error' ? 'retrieve_error' : it.bucket);

const KpiCard = ({ title, value, sub, tone }) => (
  <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${tone}`}>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
    <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    {sub != null && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const Badge = ({ it }) => {
  const b = BUCKETS[bucketOf(it)] || BUCKETS.other;
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${b.badge}`} title={b.hint}>{b.label}</span>;
};

const Reconciliation = () => {
  const { user } = useAuth();
  const role = user?.role || 'viewer';
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // { action, item }

  const load = useCallback(async (d) => {
    setLoading(true); setError(null);
    try {
      const res = await adminApi.getReconciliation({ days: d });
      setData(res.data);
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Failed to load reconciliation';
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const s = data?.summary;
  const items = data?.items || [];
  const onAct = (action, item) => setModal({ action, item });
  const onDone = () => { setModal(null); load(days); };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments Reconciliation</h1>
          <p className="text-sm text-gray-500">Orders whose Stripe state diverges from the database · your role: <span className="font-medium">{role}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Window</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="border border-gray-300 rounded-md text-sm px-3 py-2 min-h-[44px]">
            {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
          </select>
          <button onClick={() => load(days)} disabled={loading}
            className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            <ArrowPathIcon className={`h-5 w-5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <KpiCard title="Recoverable" value={usd(Math.round((s.recoverableUsd || 0) * 100))} sub={`${s.recoverable + s.expiringSoon} order(s) — capture to collect`} tone="border-amber-400" />
          <KpiCard title="Expiring <36h" value={s.expiringSoon} sub="capture before they expire" tone="border-red-500" />
          <KpiCard title="Lost / expired" value={usd(Math.round((s.lostUsd || 0) * 100))} sub={`${s.lost} order(s) — recharge possible`} tone="border-gray-400" />
          <KpiCard title="Drift" value={s.drift} sub="DB says paid, Stripe disagrees" tone="border-purple-500" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 mb-4 flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" /><span className="text-sm">{error}</span>
        </div>
      )}

      {loading && !data && <div className="text-center text-gray-500 py-12">Loading…</div>}

      {data && items.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow p-10 text-center text-gray-500">
          ✅ No payment anomalies in the last {days} days. Scanned {data.window?.scanned} order(s).
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Order', 'Customer', 'State', 'DB → Stripe', 'Amount', 'Created / Expires', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((it) => (
                  <tr key={it.paymentId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{it.orderId}{it.hasLabel && <span className="ml-2 text-xs text-green-600" title="Label created">●</span>}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 break-all">{it.email}</td>
                    <td className="px-4 py-3"><Badge it={it} /></td>
                    <td className="px-4 py-3 text-sm text-gray-600">{it.dbStatus} → <span className="font-medium">{it.stripeStatus}</span></td>
                    <td className="px-4 py-3 text-sm text-gray-900">{it.bucket === 'lost' ? usd(it.amount) : usd(it.amountCapturable || it.amount)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {it.created?.slice(0, 16).replace('T', ' ')}
                      {it.expiresAt && <div className="text-red-500">exp {it.expiresAt.slice(0, 16).replace('T', ' ')}</div>}
                    </td>
                    <td className="px-4 py-3"><ActionButtons item={it} role={role} onAct={onAct} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {items.map((it) => (
              <div key={it.paymentId} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">{it.orderId}</span><Badge it={it} />
                </div>
                <p className="text-sm text-gray-600 break-all mt-1">{it.email}</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div><span className="text-gray-400">DB→Stripe</span><br />{it.dbStatus} → <span className="font-medium">{it.stripeStatus}</span></div>
                  <div><span className="text-gray-400">Amount</span><br />{it.bucket === 'lost' ? usd(it.amount) : usd(it.amountCapturable || it.amount)}</div>
                  <div className="col-span-2 text-xs text-gray-500">
                    Created {it.created?.slice(0, 16).replace('T', ' ')}
                    {it.expiresAt && <span className="text-red-500"> · expires {it.expiresAt.slice(0, 16).replace('T', ' ')}</span>}
                  </div>
                </div>
                <div className="mt-3"><ActionButtons item={it} role={role} onAct={onAct} fullWidth /></div>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Scanned {data.window?.scanned} stripe order(s) in {days}d{data.window?.scanned >= data.window?.limit ? ` (capped at ${data.window?.limit})` : ''}.
            {!can(role, 'support') && ' Your role is read-only — capture/void/refund/recharge require support or finance.'}
          </p>
        </>
      )}

      {modal && <PaymentActionModal action={modal.action} item={modal.item} onClose={() => setModal(null)} onDone={onDone} />}
    </div>
  );
};

export default Reconciliation;
